/**
 * LevelSelectScene - 关卡选择场景
 * @description 显示所有关卡，允许玩家选择已解锁的关卡
 */

import Phaser from 'phaser';
import { GameState, getGameState, type LevelInfo } from '@/core/GameState.ts';

const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;

const COLORS = {
  background: 0x1a1a2e,
  button: 0x4a4a6a,
  buttonHover: 0x5a5a7a,
  buttonLocked: 0x2a2a4a,
  buttonCompleted: 0x4ade80,
  text: '#ffffff',
  textLocked: '#666666',
  textCompleted: '#4ade80',
  title: '#ffd700',
};

export class LevelSelectScene extends Phaser.Scene {
  private gameState: GameState;
  private levelButtons: Map<number, Phaser.GameObjects.Container> = new Map();
  private progressText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'LevelSelectScene' });
    this.gameState = getGameState();
  }

  create(): void {
    this.createBackground();
    this.createTitle();
    this.createLevelButtons();
    this.createProgressText();
    this.createResetButton();
    
    this.gameState.addListener(this.onGameStateChange.bind(this));
  }

  private createBackground(): void {
    this.cameras.main.setBackgroundColor(COLORS.background);

    const grid = this.add.graphics();
    grid.lineStyle(1, 0x2a2a4a, 0.2);
    for (let x = 0; x <= GAME_WIDTH; x += 50) {
      grid.beginPath();
      grid.moveTo(x, 0);
      grid.lineTo(x, GAME_HEIGHT);
      grid.strokePath();
    }
    for (let y = 0; y <= GAME_HEIGHT; y += 50) {
      grid.beginPath();
      grid.moveTo(0, y);
      grid.lineTo(GAME_WIDTH, y);
      grid.strokePath();
    }
  }

  private createTitle(): void {
    this.add.text(GAME_WIDTH / 2, 60, '关卡选择', {
      fontSize: '36px',
      color: COLORS.title,
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);
  }

  private createLevelButtons(): void {
    const levels = this.gameState.getLevels();
    const startX = 100;
    const startY = 150;
    const buttonWidth = 120;
    const buttonHeight = 140;
    const gapX = 140;
    const gapY = 180;

    levels.forEach((level, index) => {
      const col = index % 3;
      const row = Math.floor(index / 3);
      const x = startX + col * gapX + (row === 1 ? gapX / 2 : 0);
      const y = startY + row * gapY;

      const container = this.createLevelButton(x, y, buttonWidth, buttonHeight, level);
      this.levelButtons.set(level.id, container);
    });
  }

  private createLevelButton(
    x: number,
    y: number,
    width: number,
    height: number,
    level: LevelInfo
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    const bg = this.add.graphics();
    const bgColor = level.unlocked 
      ? (level.completed ? COLORS.buttonCompleted : COLORS.button)
      : COLORS.buttonLocked;
    
    bg.fillStyle(bgColor, level.unlocked ? 0.9 : 0.5);
    bg.fillRoundedRect(0, 0, width, height, 10);
    bg.lineStyle(3, level.unlocked ? (level.completed ? 0x4ade80 : 0x6a6a8a) : 0x3a3a5a);
    bg.strokeRoundedRect(0, 0, width, height, 10);

    const numberText = this.add.text(width / 2, 30, `${level.id}`, {
      fontSize: '48px',
      color: level.unlocked ? COLORS.text : COLORS.textLocked,
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    const nameText = this.add.text(width / 2, 80, level.name, {
      fontSize: '14px',
      color: level.unlocked ? COLORS.text : COLORS.textLocked,
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    let statusText: Phaser.GameObjects.Text;
    if (level.completed && level.bestTime !== null) {
      statusText = this.add.text(width / 2, 110, `最佳: ${level.bestTime.toFixed(2)}秒`, {
        fontSize: '12px',
        color: COLORS.textCompleted,
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    } else if (!level.unlocked) {
      statusText = this.add.text(width / 2, 110, '🔒 未解锁', {
        fontSize: '12px',
        color: COLORS.textLocked,
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    } else {
      statusText = this.add.text(width / 2, 110, '未通关', {
        fontSize: '12px',
        color: '#888888',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }

    container.add([bg, numberText, nameText, statusText]);
    container.setSize(width, height);

    if (level.unlocked) {
      container.setInteractive();
      
      container.on('pointerover', () => {
        bg.clear();
        bg.fillStyle(COLORS.buttonHover, 1);
        bg.fillRoundedRect(0, 0, width, height, 10);
        bg.strokeRoundedRect(0, 0, width, height, 10);
      });

      container.on('pointerout', () => {
        bg.clear();
        const outColor = level.completed ? COLORS.buttonCompleted : COLORS.button;
        bg.fillStyle(outColor, 0.9);
        bg.fillRoundedRect(0, 0, width, height, 10);
        bg.lineStyle(3, level.completed ? 0x4ade80 : 0x6a6a8a);
        bg.strokeRoundedRect(0, 0, width, height, 10);
      });

      container.on('pointerdown', () => {
        this.selectLevel(level);
      });
    }

    return container;
  }

  private createProgressText(): void {
    const completed = this.gameState.getCompletedCount();
    const unlocked = this.gameState.getUnlockedCount();
    const totalLevels = this.gameState.getLevels().length;
    
    this.progressText = this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT - 80,
      `进度: ${completed}/${totalLevels} 通关  |  ${unlocked}/${totalLevels} 解锁`,
      {
        fontSize: '18px',
        color: COLORS.text,
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);
  }

  private createResetButton(): void {
    const resetBtn = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 40, '重置进度', {
      fontSize: '16px',
      color: '#ff6b6b',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    resetBtn.on('pointerover', () => {
      resetBtn.setStyle({ backgroundColor: '#444444' });
    });

    resetBtn.on('pointerout', () => {
      resetBtn.setStyle({ backgroundColor: '#333333' });
    });

    resetBtn.on('pointerdown', () => {
      this.showResetConfirm();
    });
  }

  private showResetConfirm(): void {
    const overlay = this.add.graphics();
    overlay.fillStyle(0x000000, 0.7);
    overlay.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    const dialog = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2);

    const dialogBg = this.add.graphics();
    dialogBg.fillStyle(0x2a2a4a, 1);
    dialogBg.fillRoundedRect(-150, -80, 300, 160, 15);
    dialogBg.lineStyle(2, 0x6a6a8a);
    dialogBg.strokeRoundedRect(-150, -80, 300, 160, 15);

    const confirmText = this.add.text(0, -50, '确认重置所有进度？', {
      fontSize: '20px',
      color: COLORS.text,
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    const yesBtn = this.add.text(-60, 30, '确认', {
      fontSize: '16px',
      color: '#ff6b6b',
      fontFamily: 'Arial',
      backgroundColor: '#444444',
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setInteractive();

    const noBtn = this.add.text(60, 30, '取消', {
      fontSize: '16px',
      color: '#4ade80',
      fontFamily: 'Arial',
      backgroundColor: '#444444',
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setInteractive();

    dialog.add([dialogBg, confirmText, yesBtn, noBtn]);

    yesBtn.on('pointerdown', () => {
      this.gameState.resetProgress();
      overlay.destroy();
      dialog.destroy();
      this.refreshButtons();
    });

    noBtn.on('pointerdown', () => {
      overlay.destroy();
      dialog.destroy();
    });
  }

  private selectLevel(level: LevelInfo): void {
    this.gameState.setCurrentLevel(level.id);
    this.scene.start(level.sceneKey);
  }

  private onGameStateChange(): void {
    this.refreshButtons();
    this.updateProgressText();
  }

  private refreshButtons(): void {
    const levels = this.gameState.getLevels();
    
    for (const level of levels) {
      const container = this.levelButtons.get(level.id);
      if (container) {
        container.destroy();
      }
    }
    
    this.levelButtons.clear();
    this.createLevelButtons();
  }

  private updateProgressText(): void {
    const completed = this.gameState.getCompletedCount();
    const unlocked = this.gameState.getUnlockedCount();
    const totalLevels = this.gameState.getLevels().length;
    this.progressText.setText(`进度: ${completed}/${totalLevels} 通关  |  ${unlocked}/${totalLevels} 解锁`);
  }

  shutdown(): void {
    this.gameState.removeListener(this.onGameStateChange.bind(this));
  }
}